import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '../lib/api';
import { useRbacStore } from './rbacStore';
import { parseJwt } from '../utils/sso';
import { getUserAvatarUrl } from '../utils/avatar';

interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  tenantCode?: string;
  roles: string[];
  role?: string;
  avatar?: string;
  /** True for SSO users auto-provisioned with a temporary Employee role. */
  provisional?: boolean;
}

export interface PendingRequestInfo {
  email: string;
  message: string;
  isPending: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingRequest: PendingRequestInfo | null;

  login: (email: string, password: string) => Promise<{ status?: string }>;
  loginWithSso: (params: { accessToken: string; refreshToken?: string; email?: string; name?: string }) => Promise<{ status: string; message?: string }>;
  logout: () => void;
  /** Replace the access token in place (used by the silent-refresh interceptor). */
  setToken: (token: string) => void;
  register: (data: RegisterData) => Promise<{ status?: string }>;
  updateProfile: (data: Partial<User>) => void;
  clearError: () => void;
  clearPendingRequest: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  tenantName: string;
}

function normalizeUser(rawUser: any): User {
  const firstName = rawUser?.firstName ?? rawUser?.first_name ?? '';
  const lastName = rawUser?.lastName ?? rawUser?.last_name ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const roles = Array.isArray(rawUser?.roles)
    ? rawUser.roles
    : rawUser?.role
      ? [rawUser.role]
      : [];

  const rawAvatar =
    rawUser?.avatar ??
    rawUser?.avatarUrl ??
    rawUser?.avatar_url ??
    rawUser?.picture ??
    rawUser?.photoUrl ??
    rawUser?.photo_url ??
    rawUser?.image;

  const email = rawUser?.email ?? '';
  const name = rawUser?.name ?? (fullName || email || 'User');
  const avatar = rawAvatar || getUserAvatarUrl(name, email);

  return {
    id: rawUser?.id ?? '',
    email,
    name,
    tenantId: rawUser?.tenantId ?? rawUser?.tenant?.id ?? '',
    tenantCode: rawUser?.tenantCode ?? rawUser?.tenant?.code ?? '1001',
    roles,
    role: rawUser?.role || (roles.length > 0 ? roles[0] : undefined),
    avatar,
    provisional: rawUser?.provisional ?? false,
  };
}

function normalizeAuthResponse(data: any) {
  const token = data?.accessToken ?? data?.token ?? null;
  const refreshToken = data?.refreshToken ?? null;
  const user = normalizeUser(data?.user ?? {});

  return { user, token, refreshToken };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingRequest: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, pendingRequest: null });
        
        try {
          const response = await apiClient.post('/auth/login', { email, password });
          const { user, token, refreshToken } = normalizeAuthResponse(response.data);

          if (!token) {
            throw new Error('Missing authentication token in login response');
          }
          
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingRequest: null,
          });
          
          // Set auth header
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          return { status: 'authenticated' };
        } catch (error: any) {
          const errorCode = error.response?.data?.code;
          const errorMsg = error.response?.data?.message || 'Login failed';

          if (errorCode === 'ACCOUNT_PENDING_APPROVAL') {
            const pending = {
              email,
              message: errorMsg,
              isPending: true,
            };
            set({
              pendingRequest: pending,
              isLoading: false,
              error: null,
            });
            return { status: 'pending_approval' };
          }

          set({
            error: errorMsg,
            isLoading: false,
          });
          throw error;
        }
      },

      loginWithSso: async (params: { accessToken: string; refreshToken?: string; email?: string; name?: string }) => {
        set({ isLoading: true, error: null, pendingRequest: null });
        const { accessToken, refreshToken, email, name } = params;
        const decoded = parseJwt(accessToken) || {};

        const userEmail = email || decoded.email || '';
        const userName = name || decoded.name || (decoded.firstName ? `${decoded.firstName} ${decoded.lastName || ''}`.trim() : (userEmail.split('@')[0] || 'User'));

        try {
          const response = await apiClient.post('/auth/sso-login', {
            email: userEmail,
            name: userName,
            accessToken,
            refreshToken,
          });

          if (response.data?.status === 'pending_approval' || response.data?.code === 'ACCOUNT_PENDING_APPROVAL') {
            const pendingMsg = response.data?.message || 'Access request submitted. Awaiting administrator approval.';
            set({
              pendingRequest: {
                email: userEmail,
                message: pendingMsg,
                isPending: true,
              },
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return { status: 'pending_approval', message: pendingMsg };
          }

          const { user, token, refreshToken: resRefreshToken } = normalizeAuthResponse(response.data);

          if (!token) {
            throw new Error('Missing authentication token in SSO login response');
          }

          // If SSO JWT token carries Google profile picture, prioritize it
          const ssoPicture = decoded.picture || decoded.avatar || decoded.photoUrl || decoded.avatar_url || decoded.photo_url || decoded.image;
          if (ssoPicture) {
            user.avatar = ssoPicture;
          }

          set({
            user,
            token,
            refreshToken: resRefreshToken || refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            pendingRequest: null,
          });

          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          try {
            await useRbacStore.getState().loadPermissions();
          } catch (e) {
            // ignore
          }

          return { status: 'authenticated' };
        } catch (err: any) {
          const errorCode = err?.response?.data?.code;
          const errorMsg = err?.response?.data?.message || 'SSO login failed. Please try again.';

          if (errorCode === 'ACCOUNT_PENDING_APPROVAL') {
            set({
              pendingRequest: {
                email: userEmail,
                message: errorMsg,
                isPending: true,
              },
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return { status: 'pending_approval', message: errorMsg };
          }

          set({
            isAuthenticated: false,
            isLoading: false,
            error: errorMsg,
          });
          throw err;
        }
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true });
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      },

      logout: () => {
        // End the session on the server too (auth.sessions), not just in this
        // browser. Best effort: signing out locally never waits for it.
        const token = get().token;
        if (token) {
          apiClient.post('/auth/logout', null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined);
        }
        useRbacStore.getState().clearPermissions();
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          pendingRequest: null,
        });
        
        delete apiClient.defaults.headers.common['Authorization'];
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null, pendingRequest: null });
        
        try {
          const response = await apiClient.post('/auth/register', data);

          if (response.data?.status === 'pending_approval' || response.data?.code === 'ACCOUNT_PENDING_APPROVAL' || !response.data?.token) {
            const pendingMsg = response.data?.message || 'Registration submitted. Please wait for an administrator to approve your account.';
            set({
              pendingRequest: {
                email: data.email,
                message: pendingMsg,
                isPending: true,
              },
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return { status: 'pending_approval' };
          }

          const { user, token, refreshToken } = normalizeAuthResponse(response.data);

          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            pendingRequest: null,
          });
          
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          return { status: 'authenticated' };
        } catch (error: any) {
          const errorCode = error.response?.data?.code;
          const errorMsg = error.response?.data?.message || 'Registration failed';

          if (errorCode === 'ACCOUNT_PENDING_APPROVAL') {
            set({
              pendingRequest: {
                email: data.email,
                message: errorMsg,
                isPending: true,
              },
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            return { status: 'pending_approval' };
          }

          set({
            error: errorMsg,
            isLoading: false,
          });
          throw error;
        }
      },

      updateProfile: (data: Partial<User>) => {
        const user = get().user;
        if (user) {
          set({ user: { ...user, ...data } });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      clearPendingRequest: () => {
        set({ pendingRequest: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        const token = state?.token;
        if (token) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      },
    }
  )
);
